import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form, Field, useFormikContext } from "formik";
import {
  Col,
  Container,
  Row,
  Button,
  FormGroup,
  Input,
  FormFeedback,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { ReactstrapInput } from "reactstrap-formik";

import * as Yup from "yup";

import FormHelpers from "../../helpers/FormHelpers";
import ScrollToFieldError from "./Validation/ScrollToFieldError";
import InfoTooltip from "../UI/Progress/InfoTooltip";
import OptGroupSelectDropDown from "./Fields/OptGroupSelectDropDown";

import { configuratorActions } from "../../store/configurator-slice";
import { cardPreviewActions } from "../../store/card-preview-slice";

function RequestForm() {
  const dispatch = useDispatch();

  const addingToCart = useSelector((state) => state.configurator.AddingToCart);
  const currentRequest = useSelector(
    (state) => state.configurator.ConfiguratorRequest
  );
  const requestDetails = currentRequest.DonorRequest;

  const currentProductState =
    currentRequest.Products[currentRequest.CurrentProduct];
  const currentPreviewState = useSelector((state) => state.cardPreview);

  const isUpdating = requestDetails.Id;

  let defaultIntentionConfig = {
      placeholder: "Line 1 (32 character limit)\nLine 2 (32 character limit)",
      rows: 2,
      cols: 32,
      max: 64
    };

  // init state for form fields
  const [intentionConfig, setIntentionConfig] = useState(
    defaultIntentionConfig
  );

  const formRef = useRef(null);
  const intentionRef = useRef(null);

  const validationRules = {
    intention: Yup.string().required("Required"),
    cardType: Yup.string().required("Required"),
  };

  const formSchema = Yup.object().shape(validationRules);

  function formSubmitHandler(values) {
    // if request is a no card, add to cart right now and move to last step
    dispatch(configuratorActions.setRequest(values));
    dispatch(cardPreviewActions.setFieldFocus(""));
    FormHelpers.scrollToTop();
  }

  const setIntention = (cardTypeValue) => {
    dispatch(configuratorActions.setCardType(cardTypeValue));
  };

  useEffect(() => {

    if(intentionRef.current !== null) {
      const maxLength = Number(intentionConfig.max);
      const inputLength = intentionRef.current.props.value.length;
      const intention = formRef.current.values.intention;
      if (inputLength > maxLength) {
        formRef.current.setFieldValue('intention', intention.substring(0, maxLength));
        dispatch(
          cardPreviewActions.setFieldValue({
            key: "CardIntention",
            value: intention.substring(0, maxLength)
          })
        );
      }
    }
    
  }, [intentionConfig]);

  const cardTypeClickHandler = (event) => {
    setIntention(event.target.value);
  };

  const inputFocusHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus(e.target.name));
  };

  const inputBlurHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus(""));
  };

  return (
    <div className="cfg-input-form cfg-request" data-step="request">
      <Formik
        initialValues={{
          intention: requestDetails.Intention,
          cardType: requestDetails.CardTypeValue,
        }}
        validationSchema={formSchema}
        onSubmit={formSubmitHandler}
        innerRef={formRef}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form className="text-start">
            <ScrollToFieldError />
            <Container className="cfg-form-section">
              <Row>
                <Col xs="12">
                  <InfoTooltip
                    id="intentionHelp"
                    title="Help"
                    text="The person, married couple or family you are enrolling in the Seraphic Mass Association. "
                  />
                  <h4 className="required">Please Enroll:</h4>
                  <FormGroup>
                    <Input
                      name="intention"
                      type="textarea"
                      id="intention"
                      ref={intentionRef}
                      datamax={intentionConfig.max}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      value={values.intention}
                      rows={intentionConfig.rows}
                      cols={intentionConfig.cols}
                      className="cfg-intention-input parse-new-lines"
                      placeholder={intentionConfig.placeholder}
                      onKeyUp={FormHelpers.handleMultilineKeyUp}
                      onKeyDown={FormHelpers.handleMultilineKeyDown}
                      onPaste={FormHelpers.handleMultilineUpdate}
                      onChange={(e) => {
                        FormHelpers.handleMultilineUpdate(e);
                        handleChange(e);
                        dispatch(
                          cardPreviewActions.setFieldValue({
                            key: "CardIntention",
                            value: e.target.value,
                          })
                        );
                      }}
                      onFocus={(e) => {
                        inputFocusHandler(e);
                      }}
                      onBlur={(e) => {
                        FormHelpers.handleMultilineUpdate(e);
                        FormHelpers.cleanupEmptyLines(e, values, 'intention');
                        handleBlur(e);
                        inputBlurHandler();
                      }}
                      invalid={Boolean(
                        touched["intention"] && errors["intention"]
                      )}
                    />
                    {touched["intention"] && errors["intention"] ? (
                      <FormFeedback>{errors["intention"]}</FormFeedback>
                    ) : (
                      ""
                    )}
                  </FormGroup>
                </Col>
                <Col xs="12">
                  <h4 className="required">Tell Us If You Need A Card:</h4>
                  <FormGroup>
                  <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="sendtome"
                          onClick={cardTypeClickHandler}
                        />
                        Yes, please send a card to me
                      </label>
                    </div>
                    <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="sendtosomeone"
                          onClick={cardTypeClickHandler}
                        />
                        Yes, mail the card to someone for me
                      </label>
                    </div>
                    <div
                      className={`form-check ${
                        errors.cardType && touched.cardType ? "is-invalid" : ""
                      }`}
                    >
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="ecard"
                          onClick={cardTypeClickHandler}
                        />
                        Send an eCard for me
                      </label>
                    </div>
                    {errors.cardType && touched.cardType && (
                      <div className="invalid-feedback">{errors.cardType}</div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              {/* <DebugOutput
                errors={errors}
                values={FormHelpers.getValues(values)}
              /> */}
            </Container>
            <Button
              type="submit"
              size="sm"
              color="primary"
              className="float-end"
            >
              Next
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default RequestForm;
